import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { SetAuthorisationHeaders } from "components/auth/AuthorisationHeaders";
import { urls } from "configs/serviceConfig";
import { ArticleType, TagType, RecipeType, BannerType, BookletType } from "types";

export const contentApi = createApi({
  reducerPath: "contentApi",
  baseQuery: fetchBaseQuery({ baseUrl: `${urls.contentService}/api/mvf/`, prepareHeaders: SetAuthorisationHeaders }),
  endpoints: (builder) => ({
    getArticles: builder.query<
      ArticleType[],
      {
        limit: number;
        pageRequested: number;
        excludeSlug?: string;
        tagType?: TagType;
        tags?: string[];
        contentOrderSelection?: string;
      }
    >({
      query: (params) => {
        const contentOrderSelectionQuery =
          params.contentOrderSelection !== undefined ? `&contentOrderSelection=${params.contentOrderSelection}` : "";
        const excludeSlugQuery: string = params.excludeSlug !== undefined ? `&excludeSlug=${params.excludeSlug}` : "";
        const tagsParams = params.tags?.map((x) => `&${params.tagType ?? "pillarTags"}=${x}`).join("") ?? "";
        return `/articles?limit=${params.limit}&pageRequested=${params.pageRequested}${excludeSlugQuery}${tagsParams}${contentOrderSelectionQuery}`;
      },
    }),
    getArticle: builder.query<ArticleType, string>({
      query: (slug) => {
        return `/articles/${slug}`;
      },
    }),
    getBooklet: builder.query<BookletType, string>({
      query: (slug) => `/ebooklet/${slug}`,
    }),
    getBanners: builder.query<
      BannerType[],
      {
        limit: number;
        pageRequested: number;
        excludeSlug?: string;
        publicTags?: string[];
        contentOrderSelection?: string;
      }
    >({
      query: (params) => {
        const contentOrderSelectionQuery =
          params.contentOrderSelection !== undefined ? `&contentOrderSelection=${params.contentOrderSelection}` : "";
        const excludeSlugQuery: string = params.excludeSlug !== undefined ? `&excludeSlug=${params.excludeSlug}` : "";
        const tagsParams = params.publicTags?.map((x) => `&publicTags=${x}`).join("") ?? "";
        return `/banners?limit=${params.limit}&pageRequested=${params.pageRequested}${excludeSlugQuery}${tagsParams}${contentOrderSelectionQuery}`;
      },
    }),
    getBanner: builder.query<BannerType, string>({
      query: (slug) => `/banners/${slug}`,
    }),
    getRecipes: builder.query<RecipeType[], { limit: number; pageRequested: number }>({
      query: (params) => {
        return `/recipes?mostRecent=true&pageRequested=${params.pageRequested}&limit=${params.limit}`;
      },
    }),
    getRecipe: builder.query<RecipeType, string>({
      query: (slug) => {
        return `/recipes/${slug}`;
      },
    }),
    getRandomRecipes: builder.query<
      RecipeType[],
      { breakfast?: number; main?: number; snack?: number; dessert?: number }
    >({
      query: (params) => {
        return `/recipes/by-meal-type?breakfast=${params.breakfast ?? 0}&mains=${params.main ?? 0}&snacks=${
          params.snack ?? 0
        }&desserts=${params.dessert ?? 0}`;
      },
    }),
    getContent: builder.mutation<
      ArticleType[],
      {
        contentType: string;
        profileTags: string[];
        pillarTags: string[];
        stageTags: string[];
        publicTags: string[];
        skip: number;
        take: number;
      }
    >({
      query: (payload) => ({
        url: "/content/query/search",
        method: "POST",
        body: payload,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetArticlesQuery,
  useGetArticleQuery,
  useGetBannersQuery,
  useGetBannerQuery,
  useGetBookletQuery,
  useGetRecipesQuery,
  useGetRecipeQuery,
  useGetRandomRecipesQuery,
  useGetContentMutation,
} = contentApi;
